/*
 * Copyright (c) Nexign, JSC, 1992-2024.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.29.0
 */

import { Fragment, useCallback, useMemo } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EmailInput } from '@components/common/EmailInput/EmailInput';
import { getTimeZone } from '@components/Table/helpers/getTimeZone';
import { EBasketType } from '@modules/app/appConstants';
import { ELocaleKeys } from '@modules/internationalization/intConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardAdditionalReports,
} from '@modules/internationalization/intSelectors';
import { ECalendarPeriod } from '@modules/internationalization/types/common/calendar';
import { IWizardAdditionalReportsTranslation } from '@modules/internationalization/types/wizards/additionalReports';
import { WizardReportHeaderStyled } from '@modules/wizard/components/WizardReport/styled/WizardReportStyled';
import { EWizardReportParam } from '@modules/wizard/components/WizardReport/WizardReportConstants';
import { WizardReportParam } from '@modules/wizard/components/WizardReport/WizardReportParam';
import { makeSelectWizardReportParam } from '@modules/wizard/components/WizardReport/wizardReportSelectors';
import { IWizardReportParam } from '@modules/wizard/components/WizardReport/wizardReportTypes';
import { IWizardSummaryParam, WizardSummary } from '@modules/wizard/components/WizardSummary/WizardSummary';
import { wizardReportSetParamAction } from '@modules/wizard/wizardActions';
import { makeSelectWizardBasketType } from '@modules/wizard/wizardSelectors';
import { IWizardReportSetParamPayload } from '@modules/wizard/wizardTypes';
import {
    WizardAdditionalReportsStepConfirmationStyled,
} from '@pages/WizardAdditionalReports/components/styled/WizardAdditionalReportsStyled';
import { EWizardAdditionalReportsSteps } from '@pages/WizardAdditionalReports/wizardAdditionalReportsConsant';
import { IWizardAdditionalReportsReducer } from '@pages/WizardAdditionalReports/wizardAdditionalReportsReducer';
import {
    makeSelectWizardAdditionalReportsParam,
} from '@pages/WizardAdditionalReports/wizardAdditionalReportsSelectors';
import { IRootReducer } from '@reducers/rootReducer';
import { stringFormatParams } from '@utils/stringFormat';

import ICommonTranslation from '../../../modules/internationalization/types/common';
import WizardReport from '../../../modules/wizard/components/WizardReport/WizardReport';

const WizardAdditionalReportsStepConfirmation = ({
    autoReportDescription,
    autoReportHour,
    autoReportMinutes,
    autoReportMonth,
    autoReportType,
    autoReportWeek,
    basketType,
    chosenReport,
    formatChoose,
    ids,
    isCreateOrderNow,
    reportDestEmail,
    setNotificationParam,
    translation,
    translationCommon,
}: IConnectedProps & IDispatchProps) => {
    const stepTranslation = translation[EWizardAdditionalReportsSteps.ConfirmStep];

    const handleChange = useCallback(
        (payload: IWizardReportSetParamPayload) => {
            setNotificationParam(payload);
        },
        [setNotificationParam],
    );

    const additionalReportsSummaryParams: IWizardSummaryParam[] = useMemo(
        () => [
            {
                title: stepTranslation.reportType,
                values: [translation.reports[chosenReport]],
            },
            {
                title: stepTranslation.reportFormat,
                values: [formatChoose],
            },
            ...(isCreateOrderNow
                ? []
                : [
                    {
                        title: stepTranslation.reportCreationType,
                        values: [
                            `${translationCommon.calendar.period[autoReportType]}
                            ${autoReportType === ECalendarPeriod.M
                                ?  `(${autoReportMonth}${stepTranslation.reportCreationTypeOnNumberOfDay})`
                                : (autoReportType === ECalendarPeriod.W
                                    ? `(${stepTranslation.reportCreationTypeWeek[autoReportWeek]})`
                                    : '')
                            }`,
                        ],
                    },
                    {
                        title: stepTranslation.reportFilteredBy,
                        values: [
                            stringFormatParams(
                                basketType === EBasketType.MOBILE
                                    ? stepTranslation.reportFilteredBySubscribers
                                    : stepTranslation.reportFilteredByAccounts,
                                { [ELocaleKeys.number]: ids?.filter(item => item.checked).length },
                            ),
                        ],
                    },
                    {
                        title: stepTranslation.reportCreationTime,
                        values: [
                            `${autoReportHour}`.slice(-2) + ':' + `0${autoReportMinutes}`.slice(-2) + ' '
                            + getTimeZone(new Date().toISOString()),
                        ],
                    },
                ]),
            ...(!isCreateOrderNow && !!autoReportDescription
                ? [
                    {
                        title: stepTranslation.reportDescription,
                        values: [autoReportDescription],
                    },
                ]
                : []),
        ],
        [
            autoReportDescription,
            autoReportHour,
            autoReportMinutes,
            autoReportMonth,
            autoReportType,
            autoReportWeek,
            basketType,
            chosenReport,
            formatChoose,
            ids,
            isCreateOrderNow,
            stepTranslation,
            translation,
            translationCommon,
        ],
    );

    return (
        <WizardAdditionalReportsStepConfirmationStyled>
            <WizardReport notificationValidate={() => {}}>
                <Fragment>
                    <WizardReportHeaderStyled>{stepTranslation.sendDocumentTitle}</WizardReportHeaderStyled>

                    <WizardReportParam
                        dataName="wizard-report-param-emaildocs"
                        isEnabled={reportDestEmail.isEnabled}
                        label={stepTranslation.destEmailForLink}
                        setEnabled={(isEnabled: boolean) => handleChange({
                            paramType: EWizardReportParam.EMAIL_DEST, isEnabled,
                        })}
                    >
                        <EmailInput
                            dataName="wizard-report-param-emaildocs-input"
                            emailInputValue={reportDestEmail.value}
                            isValid={reportDestEmail.isValid}
                            translation={translationCommon}
                            onEmailInputChange={(value: string) =>
                                handleChange({
                                    paramType: EWizardReportParam.EMAIL_DEST,
                                    value,
                                })
                            }
                            onEmailInputValidate={(isValid: boolean) =>
                                handleChange({
                                    paramType: EWizardReportParam.EMAIL_DEST,
                                    isValid,
                                })
                            }
                        />
                    </WizardReportParam>
                </Fragment>
            </WizardReport>
            <div>
                <WizardSummary params={additionalReportsSummaryParams} />
            </div>
        </WizardAdditionalReportsStepConfirmationStyled>
    );
};

export interface IConnectedProps extends Pick<IWizardAdditionalReportsReducer,
    'autoReportDescription' |
    'autoReportHour' |
    'autoReportMinutes' |
    'autoReportMonth' |
    'autoReportType' |
    'autoReportWeek' |
    'chosenReport' |
    'formatChoose' |
    'ids' |
    'isCreateOrderNow'> {
    basketType: EBasketType;
    reportDestEmail: IWizardReportParam;
    translation: IWizardAdditionalReportsTranslation;
    translationCommon: ICommonTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    autoReportDescription: makeSelectWizardAdditionalReportsParam('autoReportDescription'),
    autoReportHour: makeSelectWizardAdditionalReportsParam('autoReportHour'),
    autoReportMinutes: makeSelectWizardAdditionalReportsParam('autoReportMinutes'),
    autoReportMonth: makeSelectWizardAdditionalReportsParam('autoReportMonth'),
    autoReportType: makeSelectWizardAdditionalReportsParam('autoReportType'),
    autoReportWeek: makeSelectWizardAdditionalReportsParam('autoReportWeek'),
    basketType: makeSelectWizardBasketType(),
    chosenReport: makeSelectWizardAdditionalReportsParam('chosenReport'),
    formatChoose: makeSelectWizardAdditionalReportsParam('formatChoose'),
    ids: makeSelectWizardAdditionalReportsParam('ids'),
    isCreateOrderNow: makeSelectWizardAdditionalReportsParam('isCreateOrderNow'),
    reportDestEmail: makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST),
    translation: makeSelectTranslationWizardAdditionalReports(),
    translationCommon: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    setNotificationParam: (payload: IWizardReportSetParamPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setNotificationParam: (payload) => dispatch(wizardReportSetParamAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardAdditionalReportsStepConfirmation);
